import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const fmt = (n) => '$' + Number(n || 0).toLocaleString();

const Admin = () => {
  const { admin, token, loading: authLoading, login, register, logout } = useAuth();
  const [tab, setTab] = useState('race');
  const [players, setPlayers] = useState([]);
  const [races, setRaces] = useState([]);
  const [currentRace, setCurrentRace] = useState(null);
  const [prizes, setPrizes] = useState({ 1: 700, 2: 400, 3: 300, 4: 200, 5: 150, 6: 100, 7: 70, 8: 40, 9: 25, 10: 15 });
  const [settings, setSettings] = useState({ monthly_prize: 4000, race_prize: 2000 });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [loginError, setLoginError] = useState('');

  // New player form
  const [newPlayer, setNewPlayer] = useState({ username: '', wagered: '' });

  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');

  // Claims state
  const [claims, setClaims] = useState([]);
  const [claimsFilter, setClaimsFilter] = useState('pending');

  useEffect(() => {
    if (admin && token) {
      fetchData();
    }
  }, [admin, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [playersData, racesData, currentRaceData, prizesData, settingsData] = await Promise.all([
        api.getPlayers(),
        api.getRaces(),
        api.getCurrentRace(),
        api.getPrizes(),
        api.getSettings()
      ]);
      
      setPlayers(Array.isArray(playersData) ? playersData : []);
      setRaces(Array.isArray(racesData) ? racesData : []);
      setCurrentRace(currentRaceData);
      
      if (Array.isArray(prizesData)) {
        const prizeMap = {};
        prizesData.forEach(p => prizeMap[p.rank] = p.amount);
        setPrizes(prizeMap);
      }
      
      if (settingsData && typeof settingsData === 'object') {
        setSettings(settingsData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await api.getUsers(token);
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchClaims = async () => {
    try {
      const response = await fetch(`https://elitebet-api-production.up.railway.app/api/claims`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setClaims(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching claims:', err);
    }
  };

  useEffect(() => {
    if (tab === 'users' && admin && token) {
      fetchUsers();
    }
    if (tab === 'claims' && admin && token) {
      fetchClaims();
    }
  }, [tab, admin, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    const result = isRegister 
      ? await register(email, password)
      : await login(email, password);
    
    if (!result.success) {
      setLoginError(result.error || 'Erreur de connexion');
    }
  };

  const handlePayPlayer = async (playerId) => {
    try {
      await api.payPlayer(playerId, token);
      fetchData();
      setMessage('Joueur marqué comme payé !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!currentRace) {
      setMessage('Aucune race active. Créez une race d\'abord.');
      return;
    }
    try {
      await api.addPlayer({
        race_id: currentRace.id,
        username: newPlayer.username,
        wagered: parseFloat(newPlayer.wagered) || 0
      }, token);
      setNewPlayer({ username: '', wagered: '' });
      fetchData();
      setMessage('Joueur ajouté !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleCreateRace = async () => {
    const today = new Date();
    const day = today.getDate();
    const startDate = today.toISOString().split('T')[0];
    const endDate = day <= 14
      ? new Date(today.getFullYear(), today.getMonth(), 14).toISOString().split('T')[0]
      : new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    
    try {
      await api.createRace({
        start_date: startDate,
        end_date: endDate,
        prize_pool: parseInt(settings.race_prize) || 2000
      }, token);
      fetchData();
      setMessage('Nouvelle race créée !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleSavePrizes = async () => {
    try {
      await api.updatePrizes(prizes, token);
      setMessage('Répartition sauvegardée !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleDeletePlayer = async (playerId) => {
    if (!window.confirm('Supprimer ce joueur ?')) return;
    try {
      const response = await fetch(`https://elitebet-api-production.up.railway.app/api/players/${playerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchData();
        setMessage('Joueur supprimé !');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleCreditUser = async () => {
    if (!selectedUser || !pointsAmount) return;
    try {
      await api.creditUser(selectedUser.id, parseInt(pointsAmount), pointsReason, token);
      setMessage(`${pointsAmount} points crédités à ${selectedUser.username} !`);
      setPointsAmount('');
      setPointsReason('');
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleDebitUser = async () => {
    if (!selectedUser || !pointsAmount) return;
    try {
      await api.debitUser(selectedUser.id, parseInt(pointsAmount), pointsReason, token);
      setMessage(`${pointsAmount} points débités de ${selectedUser.username} !`);
      setPointsAmount('');
      setPointsReason('');
      setSelectedUser(null);
      fetchUsers();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleApproveClaim = async (claimId) => {
    try {
      const response = await fetch(`https://elitebet-api-production.up.railway.app/api/claims/${claimId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMessage('Claim approuvé !');
        fetchClaims();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const handleRejectClaim = async (claimId) => {
    if (!window.confirm('Refuser ce claim ? Les points seront remboursés.')) return;
    try {
      const response = await fetch(`https://elitebet-api-production.up.railway.app/api/claims/${claimId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMessage('Claim refusé, points remboursés.');
        fetchClaims();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      setMessage('Erreur: ' + err.message);
    }
  };

  const filteredUsers = users.filter(u => 
    u.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredClaims = claims.filter(c => 
    claimsFilter === 'all' ? true : c.status === claimsFilter
  );

  const pendingClaimsCount = claims.filter(c => c.status === 'pending').length;

  // Loading state
  if (authLoading) {
    return (
      <div className="admin-page">
        <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Chargement...</p>
      </div>
    );
  }

  // Login form
  if (!admin) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>🔐 Elite<span>Bet</span> Admin</h1>
        </div>
        
        <div className="admin-card" style={{ maxWidth: '400px', margin: '2rem auto' }}>
          <h3>{isRegister ? '📝 Créer un compte Admin' : '🔑 Connexion Admin'}</h3>
          
          {loginError && (
            <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
              {loginError}
            </div>
          )}
          
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Email</label>
              <input 
                className="admin-input" 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Mot de passe</label>
              <input 
                className="admin-input" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="admin-btn" style={{ width: '100%' }}>
              {isRegister ? 'Créer le compte' : 'Se connecter'}
            </button>
          </form>
          
          <p style={{ color: '#666', textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem' }}>
            {isRegister ? 'Déjà un compte ?' : 'Premier admin ?'}{' '}
            <button 
              onClick={() => setIsRegister(!isRegister)} 
              style={{ background: 'none', border: 'none', color: '#EFABED', cursor: 'pointer' }}
            >
              {isRegister ? 'Se connecter' : 'Créer un compte'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Admin panel
  const pool = parseInt(settings.race_prize) || 2000;
  const total = Object.values(prizes).reduce((a, b) => a + Number(b), 0);
  const valid = total === pool;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>🔐 Elite<span>Bet</span> Admin</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: '#888', fontSize: '0.85rem' }}>{admin.email}</span>
          <button onClick={logout} className="admin-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', background: '#ef4444' }}>
            Déconnexion
          </button>
        </div>
      </div>

      {message && (
        <div style={{ background: 'rgba(74,222,128,0.1)', color: '#4ade80', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem' }}>
          {message}
        </div>
      )}

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'race' ? 'active' : ''}`} onClick={() => setTab('race')}>🏆 Race Active</button>
        <button className={`admin-tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>📜 Historique</button>
        <button className={`admin-tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>👥 Utilisateurs</button>
        <button className={`admin-tab ${tab === 'claims' ? 'active' : ''}`} onClick={() => setTab('claims')}>
          🎁 Claims {pendingClaimsCount > 0 && <span style={{ background: '#ef4444', color: '#fff', borderRadius: '50%', padding: '0.1rem 0.4rem', fontSize: '0.7rem', marginLeft: '0.3rem' }}>{pendingClaimsCount}</span>}
        </button>
        <button className={`admin-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>⚙️ Paramètres</button>
      </div>

      {loading ? (
        <p style={{ color: '#888', textAlign: 'center', padding: '2rem' }}>Chargement...</p>
      ) : (
        <>
          {tab === 'race' && (
            <>
              <div className="admin-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>🏆 Race en cours</h3>
                  {!currentRace && (
                    <button onClick={handleCreateRace} className="admin-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>
                      + Nouvelle Race
                    </button>
                  )}
                </div>
                
                {currentRace ? (
                  <p style={{ color: '#888', marginBottom: '1rem' }}>
                    {currentRace.start_date} → {currentRace.end_date} | Prize Pool: <strong style={{ color: '#EFABED' }}>{fmt(currentRace.prize_pool)}</strong>
                  </p>
                ) : (
                  <p style={{ color: '#ef4444', marginBottom: '1rem' }}>Aucune race active. Créez-en une !</p>
                )}

                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Username</th>
                      <th>Wagered</th>
                      <th>Prize</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.length === 0 ? (
                      <tr><td colSpan="6" style={{ color: '#666', textAlign: 'center' }}>Aucun joueur</td></tr>
                    ) : (
                      players.map((player, index) => {
                        const rank = index + 1;
                        return (
                          <tr key={player.id}>
                            <td>#{rank}</td>
                            <td style={{ color: '#EFABED', fontWeight: 600 }}>{player.username}</td>
                            <td>{fmt(player.wagered)}</td>
                            <td style={{ color: '#4ade80' }}>{rank <= 10 ? fmt(prizes[rank] || 0) : '—'}</td>
                            <td>
                              <span className={`status-badge ${player.payment_status}`}>
                                {player.payment_status === 'paid' ? '✓ Payé' : '⏳ Attente'}
                              </span>
                            </td>
                            <td style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                onClick={() => handlePayPlayer(player.id)} 
                                className="admin-btn" 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                                disabled={player.payment_status === 'paid'}
                              >
                                💸 Payer
                              </button>
                              <button 
                                onClick={() => handleDeletePlayer(player.id)} 
                                className="admin-btn" 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', background: '#ef4444' }}
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="admin-card">
                <h3>➕ Ajouter un joueur</h3>
                <form onSubmit={handleAddPlayer} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <input 
                    className="admin-input" 
                    placeholder="Username" 
                    value={newPlayer.username}
                    onChange={(e) => setNewPlayer({ ...newPlayer, username: e.target.value })}
                    style={{ flex: '1', minWidth: '150px' }}
                    required
                  />
                  <input 
                    className="admin-input" 
                    placeholder="Wagered ($)" 
                    type="number"
                    value={newPlayer.wagered}
                    onChange={(e) => setNewPlayer({ ...newPlayer, wagered: e.target.value })}
                    style={{ width: '150px' }}
                    required
                  />
                  <button type="submit" className="admin-btn">Ajouter</button>
                </form>
              </div>
            </>
          )}

          {tab === 'history' && (
            <div className="admin-card">
              <h3>📜 Historique des Races</h3>
              <table className="admin-table">
                <thead><tr><th>Période</th><th>Prize Pool</th><th>Status</th></tr></thead>
                <tbody>
                  {races.filter(r => r.status === 'completed').length === 0 ? (
                    <tr><td colSpan="3" style={{ color: '#666', textAlign: 'center' }}>Aucune race terminée</td></tr>
                  ) : (
                    races.filter(r => r.status === 'completed').map(race => (
                      <tr key={race.id}>
                        <td>{race.start_date} → {race.end_date}</td>
                        <td>{fmt(race.prize_pool)}</td>
                        <td><span className="status-badge paid">✓ Terminé</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'users' && (
            <>
              <div className="admin-card">
                <h3>👥 Utilisateurs inscrits ({users.length})</h3>
                
                <input 
                  className="admin-input" 
                  placeholder="🔍 Rechercher un utilisateur..." 
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{ marginBottom: '1rem' }}
                />

                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Avatar</th>
                      <th>Pseudo</th>
                      <th>Email</th>
                      <th>Points</th>
                      <th>Inscription</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr><td colSpan="6" style={{ color: '#666', textAlign: 'center' }}>Aucun utilisateur</td></tr>
                    ) : (
                      filteredUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            {user.avatar ? (
                              <img 
                                src={user.discord_id 
                                  ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`
                                  : user.avatar
                                } 
                                alt="" 
                                style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                              />
                            ) : (
                              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#2a2a2e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👤</div>
                            )}
                          </td>
                          <td style={{ color: '#EFABED', fontWeight: 600 }}>{user.username}</td>
                          <td style={{ color: '#888', fontSize: '0.85rem' }}>{user.email || '—'}</td>
                          <td style={{ color: '#4ade80', fontWeight: 600 }}>{user.points?.toLocaleString() || 0}</td>
                          <td style={{ color: '#888', fontSize: '0.85rem' }}>{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                          <td>
                            <button 
                              onClick={() => setSelectedUser(user)} 
                              className="admin-btn" 
                              style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                            >
                              💰 Gérer
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {selectedUser && (
                <div className="admin-card">
                  <h3>💰 Gérer les points de {selectedUser.username}</h3>
                  <p style={{ color: '#888', marginBottom: '1rem' }}>
                    Solde actuel : <strong style={{ color: '#4ade80' }}>{selectedUser.points?.toLocaleString() || 0} points</strong>
                  </p>
                  
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <input 
                      className="admin-input" 
                      placeholder="Montant" 
                      type="number"
                      value={pointsAmount}
                      onChange={(e) => setPointsAmount(e.target.value)}
                      style={{ width: '120px' }}
                    />
                    <input 
                      className="admin-input" 
                      placeholder="Raison (optionnel)" 
                      value={pointsReason}
                      onChange={(e) => setPointsReason(e.target.value)}
                      style={{ flex: '1', minWidth: '200px' }}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                      onClick={handleCreditUser} 
                      className="admin-btn" 
                      style={{ background: '#4ade80' }}
                      disabled={!pointsAmount}
                    >
                      ➕ Créditer
                    </button>
                    <button 
                      onClick={handleDebitUser} 
                      className="admin-btn" 
                      style={{ background: '#ef4444' }}
                      disabled={!pointsAmount}
                    >
                      ➖ Débiter
                    </button>
                    <button 
                      onClick={() => setSelectedUser(null)} 
                      className="admin-btn" 
                      style={{ background: '#3a3a3e' }}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'claims' && (
            <div className="admin-card">
              <h3>🎁 Demandes de récompenses</h3>
              
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button 
                  className={`admin-tab ${claimsFilter === 'pending' ? 'active' : ''}`}
                  onClick={() => setClaimsFilter('pending')}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                >
                  ⏳ En attente ({claims.filter(c => c.status === 'pending').length})
                </button>
                <button 
                  className={`admin-tab ${claimsFilter === 'approved' ? 'active' : ''}`}
                  onClick={() => setClaimsFilter('approved')}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                >
                  ✓ Approuvés
                </button>
                <button 
                  className={`admin-tab ${claimsFilter === 'rejected' ? 'active' : ''}`}
                  onClick={() => setClaimsFilter('rejected')}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                >
                  ✗ Refusés
                </button>
                <button 
                  className={`admin-tab ${claimsFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setClaimsFilter('all')}
                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                >
                  Tous
                </button>
              </div>

              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Utilisateur</th>
                    <th>Récompense</th>
                    <th>Pseudo Casino</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClaims.length === 0 ? (
                    <tr><td colSpan="6" style={{ color: '#666', textAlign: 'center' }}>Aucune demande</td></tr>
                  ) : (
                    filteredClaims.map(claim => (
                      <tr key={claim.id}>
                        <td style={{ color: '#888', fontSize: '0.85rem' }}>
                          {new Date(claim.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td style={{ color: '#EFABED', fontWeight: 600 }}>{claim.username}</td>
                        <td>
                          <div>{claim.reward_name}</div>
                          <div style={{ color: '#888', fontSize: '0.8rem' }}>{claim.reward_cost} points</div>
                        </td>
                        <td style={{ color: '#4ade80', fontWeight: 600 }}>{claim.casino_username}</td>
                        <td>
                          <span className={`status-badge ${claim.status === 'approved' ? 'paid' : claim.status === 'pending' ? 'pending' : ''}`} style={claim.status === 'rejected' ? { background: 'rgba(239,68,68,0.2)', color: '#ef4444' } : {}}>
                            {claim.status === 'pending' ? '⏳ En attente' : claim.status === 'approved' ? '✓ Approuvé' : '✗ Refusé'}
                          </span>
                        </td>
                        <td>
                          {claim.status === 'pending' && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                onClick={() => handleApproveClaim(claim.id)} 
                                className="admin-btn" 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', background: '#4ade80' }}
                              >
                                ✓
                              </button>
                              <button 
                                onClick={() => handleRejectClaim(claim.id)} 
                                className="admin-btn" 
                                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', background: '#ef4444' }}
                              >
                                ✗
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'settings' && (
            <>
              <div className="admin-card">
                <h3>💰 Montants</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: '500px' }}>
                  <div>
                    <label style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Mensuel ($)</label>
                    <input 
                      className="admin-input" 
                      type="number" 
                      value={settings.monthly_prize || 4000} 
                      onChange={(e) => setSettings({ ...settings, monthly_prize: e.target.value })}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Par Race ($)</label>
                    <input 
                      className="admin-input" 
                      type="number" 
                      value={settings.race_prize || 2000} 
                      onChange={(e) => setSettings({ ...settings, race_prize: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="admin-card">
                <h3>🏆 Répartition</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem' }}>
                  {[1,2,3,4,5,6,7,8,9,10].map((r) => (
                    <div key={r}>
                      <label style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                        {r === 1 ? '🥇' : r === 2 ? '🥈' : r === 3 ? '🥉' : `#${r}`}
                      </label>
                      <input 
                        className="admin-input" 
                        type="number" 
                        value={prizes[r] || 0} 
                        onChange={(e) => setPrizes({ ...prizes, [r]: parseInt(e.target.value) || 0 })} 
                        style={{ padding: '0.5rem' }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: valid ? 'rgba(74,222,128,0.1)' : 'rgba(239,68,68,0.1)', borderRadius: '8px' }}>
                  <span style={{ color: valid ? '#4ade80' : '#ef4444' }}>Total: {fmt(total)} / {fmt(pool)} {valid ? '✓' : '⚠️'}</span>
                </div>
              </div>
              <button onClick={handleSavePrizes} className="admin-btn">💾 Sauvegarder</button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Admin;
